// =============================================================================
// Career listing categories + smart-fill templates
// =============================================================================
//
// Slugs MUST stay in sync with CAREER_CATEGORIES in /app/backend/models.py.
// The `template` block is what the admin's "Generate from template" button
// will splat into the listing form. Admin can then edit before publishing.
// =============================================================================

export const CAREER_CATEGORIES = [
  { slug: "sales", label: "Sales" },
  { slug: "superintendent", label: "Superintendent" },
  { slug: "maintenance-tech", label: "Maintenance Tech" },
  { slug: "appointment-setter", label: "Appointment Setter" },
  { slug: "project-manager", label: "Project Manager" },
  { slug: "roofing-sales", label: "Roofing Sales" },
  { slug: "admin-office", label: "Admin / Office" },
  { slug: "skilled-labor", label: "Skilled Labor" },
  { slug: "subcontractor-coordinator", label: "Subcontractor Coordinator" },
];

export const CATEGORY_LABEL = Object.fromEntries(
  CAREER_CATEGORIES.map((c) => [c.slug, c.label])
);

// Light-weight UX dropdowns that show up on both the public application form
// and the admin listing editor.
export const COMPENSATION_TYPES = [
  "Commission",
  "Hourly",
  "Salary",
  "Base + Commission",
  "Per Appointment",
  "Per Project",
  "Contract",
  "Other",
];

export const EMPLOYMENT_TYPES = [
  "Full-time",
  "Part-time",
  "Contract",
  "1099",
  "W2",
  "Internship / entry-level",
];

export const LOCATION_OPTIONS = [
  "Austin, TX",
  "Central Texas",
  "Remote / field-based",
  "Hybrid",
  "Specific city / service area",
];

export const EXPERIENCE_LEVELS = [
  "Entry level",
  "1–3 years",
  "3–5 years",
  "5–10 years",
  "10+ years",
];

// =============================================================================
// Smart templates (per user spec)
// =============================================================================
// Each template is just multi-line text we splat into a textarea — admins
// can edit any field before publishing. Bullets use `•` so they survive a
// plain-text round-trip without depending on a rich-text editor.
//
// Field schema for each template:
//   title                — default position title
//   overview             — long-form paragraph
//   responsibilities     — bullet list
//   good_fit_for         — bullet list
//   preferred_experience — bullet list (may be empty)
//   compensation_notes   — paragraph describing pay structure
//   do_not_promise       — Expectations + Do-Not-Promise / Important notes
//   growth_path          — paragraph on internal growth trajectory
// =============================================================================

function bullets(lines) {
  return lines.map((l) => `• ${l}`).join("\n");
}

// Helper: combine an "Expectations" block with a "Do Not Promise" block into
// the single `do_not_promise` textarea. Each section gets a short header line
// so the admin can keep them separate visually but still edit them as one
// block. Pass null/empty to omit a section.
function combineExpectations({ expectations = null, doNot = null, important = null, extras = null }) {
  const parts = [];
  if (expectations) {
    parts.push("Expectations:");
    parts.push(typeof expectations === "string" ? expectations : bullets(expectations));
  }
  if (doNot) {
    if (parts.length) parts.push("");
    parts.push("Do Not Promise:");
    parts.push(typeof doNot === "string" ? doNot : bullets(doNot));
  }
  if (important) {
    if (parts.length) parts.push("");
    parts.push("Important:");
    parts.push(typeof important === "string" ? important : bullets(important));
  }
  if (extras) {
    // Array of { header, body } objects for category-specific notes
    extras.forEach(({ header, body }) => {
      if (parts.length) parts.push("");
      parts.push(`${header}:`);
      parts.push(typeof body === "string" ? body : bullets(body));
    });
  }
  return parts.join("\n");
}

export const SMART_TEMPLATES = {
  // ---------------------------------------------------------------- SALES ---
  sales: {
    title: "Sales Representative",
    overview:
      "Black Ridge Construction is looking for motivated sales professionals who can help homeowners, property owners, investors, and commercial clients understand their options for roofing, remodeling, repairs, maintenance memberships, and construction services. This role is built for someone who is comfortable talking to people, following up consistently, learning construction services, and helping qualified prospects move from interest to signed project or membership.",
    responsibilities: bullets([
      "Generate and follow up with homeowner, investor, and commercial leads",
      "Present Black Ridge services clearly and professionally",
      "Use company sales materials, website pages, brochures, and pricing guidelines during conversations",
      "Qualify prospects based on scope, budget, urgency, location, property type, and decision-making timeline",
      "Set consultations, inspections, maintenance plan calls, and estimate appointments",
      "Follow up with open estimates, past conversations, and previous clients",
      "Promote roofing services, maintenance memberships, remodels, repairs, commercial services, and project opportunities",
      "Track calls, texts, appointments, follow-ups, and outcomes",
      "Communicate clearly with leadership before promising pricing, timelines, scope, discounts, or production dates",
      "Help collect project photos, testimonials, and client feedback when appropriate",
      "Maintain professional communication with clients, trade partners, and internal team members",
    ]),
    good_fit_for: bullets([
      "Someone comfortable speaking with homeowners and business owners",
      "Someone who can follow a process and stay organized",
      "Someone interested in construction, roofing, remodeling, maintenance, and property services",
      "Someone who wants performance-based earning potential",
      "Someone who can handle rejection and keep moving",
      "Someone who can build trust without being pushy",
      "Someone who understands that follow-up is a major part of sales",
    ]),
    preferred_experience: bullets([
      "Sales experience",
      "Door-to-door experience",
      "Roofing or construction sales",
      "Customer service",
      "Appointment setting",
      "CRM or lead tracking",
      "Residential construction knowledge",
      "Commercial or property management sales experience is a plus",
    ]),
    compensation_notes:
      "Compensation may include commission, appointment bonuses, project-based pay, base plus commission, or other structure depending on the specific listing. Final compensation details should be shown in the Compensation Details field.",
    do_not_promise: combineExpectations({
      doNot: [
        "Guaranteed pricing without approval",
        "Guaranteed project start dates",
        "Insurance claim outcomes",
        "Unlimited repair coverage",
        "Services outside Black Ridge's approved scope",
        "Discounts, financing, or payment terms without approval",
        "Licensed trade work unless confirmed by leadership",
      ],
    }),
    growth_path:
      "A successful sales team member may grow into a closer, sales manager, project consultant, roofing specialist, commercial account representative, or leadership role as Black Ridge expands.",
  },

  // -------------------------------------------------------- SUPERINTENDENT ---
  superintendent: {
    title: "Construction Superintendent",
    overview:
      "Black Ridge Construction is looking for a field-focused superintendent to help coordinate residential and commercial construction projects, subcontractors, schedules, materials, jobsite quality, safety expectations, and client communication. This role is built for someone who understands construction sequencing, can communicate clearly with trades and clients, and can protect the company's standards in the field.",
    responsibilities: bullets([
      "Coordinate daily jobsite activity",
      "Communicate with subcontractors, crews, clients, vendors, and leadership",
      "Verify scope of work before and during production",
      "Help schedule trades, deliveries, inspections, walkthroughs, and project milestones",
      "Track jobsite progress and report issues quickly",
      "Review workmanship and identify corrections",
      "Maintain jobsite cleanliness and safety expectations",
      "Take progress photos and document completed work",
      "Confirm materials, access, utilities, and site readiness",
      "Help prevent scope creep and unapproved work",
      "Escalate change orders, delays, material issues, and client concerns",
      "Protect project margins, timelines, and company standards",
      "Help close out punch-list items",
      "Support both residential and commercial projects when needed",
    ]),
    good_fit_for: bullets([
      "Experienced construction field leaders",
      "Organized communicators",
      "People who can manage trades without creating unnecessary conflict",
      "People who understand quality control and jobsite sequencing",
      "People who can solve problems in the field",
      "People comfortable using photos, checklists, schedules, and documentation",
      "People who can represent Black Ridge professionally in front of clients",
    ]),
    preferred_experience: bullets([
      "Residential remodeling",
      "Roofing",
      "Exterior repairs",
      "Commercial remodels",
      "Tenant improvements",
      "Maintenance or punch-list work",
      "Scheduling and subcontractor coordination",
      "Reading scopes, estimates, plans, or job notes",
      "Coordinating multiple trades",
      "Handling client walkthroughs",
    ]),
    compensation_notes:
      "Compensation may be hourly, salary, contract, per-project, or performance-based depending on the listing. Final compensation details should be shown in the Compensation Details field.",
    do_not_promise: combineExpectations({
      expectations: [
        "Show up prepared and on time",
        "Keep leadership informed before problems grow",
        "Document the jobsite with photos and notes",
        "Avoid approving extra work without written approval",
        "Maintain professional communication with clients and trades",
        "Protect the company's reputation and project margins",
      ],
    }),
    growth_path:
      "A strong superintendent may grow into project manager, production manager, commercial superintendent, operations manager, or senior field leadership.",
  },

  // ----------------------------------------------------- MAINTENANCE TECH ---
  "maintenance-tech": {
    title: "Maintenance Technician",
    overview:
      "Black Ridge Construction is looking for a maintenance technician to support our home maintenance membership program, property care visits, punch-list work, and small approved repair tasks. This role is built around structured inspections, documentation, routine maintenance, client communication, and knowing when to escalate larger work to the contractor-service side.",
    responsibilities: bullets([
      "Complete scheduled maintenance visits",
      "Follow Black Ridge maintenance checklists",
      "Document visible property conditions with photos and notes",
      "Replace client-supplied HVAC filters where accessible",
      "Test accessible smoke/CO devices using test buttons",
      "Check visible leaks or moisture concerns under sinks and near utility areas",
      "Observe exterior conditions such as roofline, gutters, drainage, trim, siding, caulking, doors, and windows",
      "Perform minor approved maintenance tasks within plan limits",
      "Identify Pass, Watch, Act Now, and Deferred items",
      "Communicate deferred items clearly",
      "Recommend add-ons or contractor-service handoffs when needed",
      "Avoid licensed, unsafe, structural, roofing, electrical, plumbing, or HVAC repair work unless properly approved and assigned",
      "Keep reports clear, professional, and completed as quickly as possible",
      "Maintain clean work areas and respectful homeowner communication",
    ]),
    good_fit_for: bullets([
      "Reliable field technicians",
      "People with handyman, punch-list, or maintenance experience",
      "People who can follow a checklist",
      "People who can communicate clearly with homeowners",
      "People who understand when not to touch something",
      "People who value documentation and professionalism",
      "People who can work independently without cutting corners",
    ]),
    preferred_experience: bullets([
      "Basic home maintenance",
      "Drywall touch-ups",
      "Caulking",
      "Door and hardware adjustments",
      "Trim repair",
      "Painting touch-ups",
      "Exterior observations",
      "Property inspection or punch-list work",
      "Basic tool use",
      "Customer-facing field work",
    ]),
    compensation_notes:
      "Compensation may be hourly, per visit, salary, or contract depending on the listing. Final compensation details should be shown in the Compensation Details field.",
    do_not_promise: combineExpectations({
      important:
        "This role is not open-ended handyman labor. Maintenance techs inspect, maintain, document, quote, and escalate according to Black Ridge standards.",
    }),
    growth_path:
      "A strong maintenance technician may grow into senior maintenance tech, maintenance route lead, superintendent, project coordinator, or field operations role.",
  },

  // ----------------------------------------------------- APPOINTMENT SETTER ---
  "appointment-setter": {
    title: "Appointment Setter",
    overview:
      "Black Ridge Construction is looking for an appointment setter to help create qualified conversations with homeowners, property owners, investors, and potential commercial clients. This role is ideal for someone getting into sales who needs structure, repetition, and a clear process to follow. The appointment setter's main job is to start conversations, qualify interest, and book appointments for estimates, inspections, maintenance membership calls, or project consultations.",
    responsibilities: bullets([
      "Start conversations with homeowners, local businesses, investors, and property owners",
      "Door knock, call, text, message, or network depending on the assigned strategy",
      "Introduce Black Ridge Construction professionally",
      "Ask qualifying questions about roofing, repairs, remodeling, maintenance plans, or property needs",
      "Set qualified appointments with decision-makers",
      "Confirm appointment details and contact information",
      "Track daily outreach, conversations, appointments set, and appointment outcomes",
      "Follow approved scripts and improve through practice",
      "Pivot to maintenance memberships when a prospect is not ready for a larger project",
      "Follow up with prospects who requested information",
      "Represent Black Ridge professionally in neighborhoods, groups, and online conversations",
    ]),
    good_fit_for: bullets([
      "Someone new to sales who wants structure",
      "Someone comfortable starting conversations",
      "Someone coachable",
      "Someone reliable and consistent",
      "Someone who can handle rejection without taking it personally",
      "Someone interested in growing into a larger sales role",
      "Someone who can work part-time or flexible hours while learning",
    ]),
    preferred_experience: bullets([
      "Customer service",
      "Retail",
      "Coffee shop, hospitality, or service industry",
      "Door-to-door experience is helpful but not required",
      "Social media outreach",
      "Basic phone/text communication",
      "Local networking",
    ]),
    compensation_notes:
      "Compensation may include per qualified appointment pay, signed-contract bonuses, hourly pay, commission, or a hybrid structure depending on the listing. Final compensation details should be shown in the Compensation Details field.",
    do_not_promise: combineExpectations({
      doNot: [
        "Final pricing",
        "Start dates",
        "Free work",
        "Insurance outcomes",
        "Services outside approved Black Ridge scope",
        "Discounts without approval",
      ],
      extras: [
        {
          header: "Appointment Quality Standards",
          body: [
            "Correct contact information",
            "Property address or service area",
            "Clear service interest",
            "Decision-maker availability",
            "Reasonable project need or maintenance plan interest",
            "Confirmed appointment time",
          ],
        },
      ],
    }),
    growth_path:
      "An appointment setter can grow into sales representative, roofing sales, project consultant, closer, or local territory lead.",
  },

  // ------------------------------------------------------- PROJECT MANAGER ---
  "project-manager": {
    title: "Project Manager",
    overview:
      "Black Ridge Construction is looking for a project manager to help oversee residential, commercial, roofing, remodeling, maintenance, and repair projects from planning through completion. This role requires strong communication, organization, scheduling, scope control, and the ability to coordinate clients, trades, vendors, and internal leadership.",
    responsibilities: bullets([
      "Review project scope, estimates, plans, notes, and client expectations before production",
      "Coordinate scheduling with subcontractors, vendors, crews, and clients",
      "Track project timelines, change orders, materials, and completion milestones",
      "Communicate project updates to clients and leadership",
      "Help manage job costs and protect margins",
      "Coordinate site walks, inspections, progress checks, and punch lists",
      "Document project progress with photos and notes",
      "Identify delays, scope issues, trade conflicts, or client concerns early",
      "Help resolve problems before they affect the project outcome",
      "Support superintendents and field crews",
      "Confirm completed work aligns with approved scope",
      "Help prepare closeout notes and final walkthroughs",
    ]),
    good_fit_for: bullets([
      "Organized construction professionals",
      "Strong communicators",
      "People who can manage multiple moving parts",
      "People who can balance client service with company standards",
      "People who understand project documentation",
      "People who can think ahead and prevent problems",
      "People comfortable coordinating both residential and commercial work",
    ]),
    preferred_experience: bullets([
      "Construction project management",
      "Remodeling",
      "Roofing",
      "Commercial remodels",
      "Estimating or scope review",
      "Scheduling subcontractors",
      "Client communication",
      "Change order management",
      "Job costing",
      "Field documentation",
    ]),
    compensation_notes:
      "Compensation may be salary, hourly, per-project, performance-based, or hybrid depending on the listing. Final compensation details should be shown in the Compensation Details field.",
    do_not_promise: combineExpectations({
      expectations: [
        "Keep projects organized",
        "Communicate clearly and consistently",
        "Protect scope, schedule, and margin",
        "Avoid undocumented changes",
        "Escalate issues early",
        "Maintain professional client and trade relationships",
      ],
    }),
    growth_path:
      "A successful project manager may grow into senior project manager, production manager, operations manager, commercial project manager, or leadership role.",
  },

  // --------------------------------------------------------- ROOFING SALES ---
  "roofing-sales": {
    title: "Roofing Sales Representative",
    overview:
      "Black Ridge Construction is looking for roofing sales professionals to help homeowners and property owners with roof inspections, storm damage concerns, roof repairs, roof replacements, RoofWatch memberships, and related exterior repair opportunities. This role is built for someone who can communicate clearly, document roof-related concerns, follow up consistently, and help clients understand their options without overpromising.",
    responsibilities: bullets([
      "Generate and follow up with roofing leads",
      "Set roof inspections and storm follow-up appointments",
      "Help identify visible roof concerns from safe and approved vantage points",
      "Document roof-related findings with photos and notes when assigned",
      "Present roof repair, roof replacement, and RoofWatch options",
      "Explain the difference between inspection, repair, replacement, and maintenance",
      "Coordinate with leadership before making pricing or scope commitments",
      "Follow up with homeowners after storms",
      "Communicate clearly with production and estimating teams",
      "Promote RoofWatch Residential and commercial roofing maintenance options",
      "Track leads, estimates, follow-ups, and outcomes",
      "Maintain professional and policy-safe communication around insurance-related work",
    ]),
    good_fit_for: bullets([
      "Someone comfortable with homeowner conversations",
      "Someone interested in roofing and exterior construction",
      "Someone who can follow a sales process",
      "Someone who understands documentation matters",
      "Someone who can work after storms or high-demand periods",
      "Someone who can handle rejection and follow up professionally",
    ]),
    preferred_experience: bullets([
      "Roofing sales",
      "Construction sales",
      "Storm restoration",
      "Door-to-door sales",
      "Insurance-related roofing communication",
      "Residential exterior sales",
      "CRM or lead tracking",
      "Basic roof system knowledge",
    ]),
    compensation_notes:
      "Compensation may include commission, base plus commission, inspection bonuses, project-based pay, or other structure depending on the listing. Final compensation details should be shown in the Compensation Details field.",
    do_not_promise: combineExpectations({
      doNot: [
        "Insurance claim approval",
        "Covered damage",
        "Free roof replacement",
        "Guaranteed start dates",
        "Final pricing without approval",
        "Public adjusting services",
        "Engineering opinions",
        "Roof safety conclusions beyond approved company standards",
      ],
    }),
    growth_path:
      "A successful roofing sales rep may grow into senior roofing consultant, sales manager, storm response lead, commercial roofing account rep, or full construction sales role.",
  },

  // --------------------------------------------------------- ADMIN / OFFICE ---
  "admin-office": {
    title: "Admin / Office Support",
    overview:
      "Black Ridge Construction is looking for an admin/office support team member to help keep operations organized across sales, construction, maintenance memberships, client communication, scheduling, documentation, and office workflows. This role is built for someone detail-oriented, reliable, organized, and comfortable communicating with clients, team members, vendors, and trade partners.",
    responsibilities: bullets([
      "Answer calls, emails, messages, and form submissions professionally",
      "Help schedule consultations, inspections, maintenance visits, and project appointments",
      "Organize client information, project notes, photos, and documents",
      "Support sales follow-up and estimate tracking",
      "Help maintain CRM, spreadsheets, calendars, and admin systems",
      "Assist with invoice, payment, deposit, and document organization where assigned",
      "Track applications for careers, subcontractors, and project opportunities",
      "Help manage maintenance plan records and visit reports",
      "Coordinate communication between clients, leadership, field teams, and trade partners",
      "Help keep internal processes clean, updated, and consistent",
      "Assist with social media, website updates, or basic marketing tasks if assigned",
    ]),
    good_fit_for: bullets([
      "Organized and detail-oriented people",
      "Strong communicators",
      "People comfortable with phone, email, and text communication",
      "People who can follow systems",
      "People who can handle multiple small tasks without losing track",
      "People who want to support a growing construction company",
    ]),
    preferred_experience: bullets([
      "Office administration",
      "Construction admin",
      "Scheduling",
      "Customer service",
      "CRM or spreadsheet work",
      "Basic bookkeeping support",
      "Document organization",
      "Social media or website admin is a plus",
      "Dispatch or service coordination is a plus",
    ]),
    compensation_notes:
      "Compensation may be hourly, salary, part-time, full-time, or contract depending on the listing. Final compensation details should be shown in the Compensation Details field.",
    do_not_promise: combineExpectations({
      expectations: [
        "Keep communication professional",
        "Protect client information",
        "Stay organized",
        "Ask questions early",
        "Document tasks clearly",
        "Support the team without creating bottlenecks",
      ],
    }),
    growth_path:
      "An admin/office team member may grow into office manager, operations coordinator, service coordinator, project coordinator, marketing coordinator, or executive assistant role.",
  },

  // ---------------------------------------------------------- SKILLED LABOR ---
  "skilled-labor": {
    title: "Skilled Construction Labor",
    overview:
      "Black Ridge Construction is looking for skilled labor support for residential and commercial projects, repairs, remodeling, roofing support, exterior work, maintenance, punch-list work, and jobsite production. This role is for reliable workers who can perform quality work, follow directions, show up prepared, and help keep projects moving safely and efficiently.",
    responsibilities: bullets([
      "Support construction, remodeling, repair, maintenance, and exterior projects",
      "Perform assigned labor tasks according to scope and supervisor direction",
      "Assist with demo, cleanup, material handling, prep, and jobsite organization",
      "Support drywall, paint, trim, framing, exterior repairs, or punch-list work depending on experience",
      "Maintain clean and safe work areas",
      "Use tools responsibly and safely",
      "Communicate issues, missing materials, or field problems quickly",
      "Follow superintendent or project manager direction",
      "Avoid unapproved extra work or scope changes",
      "Respect client property and jobsite standards",
      "Take pride in clean, professional workmanship",
    ]),
    good_fit_for: bullets([
      "Reliable workers with hands-on construction experience",
      "People who show up on time",
      "People who can follow direction",
      "People who want to grow in construction",
      "People who work safely and respectfully",
      "People who can learn Black Ridge standards",
    ]),
    preferred_experience: bullets([
      "Demo",
      "Drywall",
      "Painting",
      "Trim",
      "Framing",
      "Roofing support",
      "Flooring",
      "Tile",
      "Exterior repairs",
      "Punch-list work",
      "Maintenance",
      "General construction labor",
    ]),
    compensation_notes:
      "Compensation may be hourly, daily, per-project, contract, W2, or 1099 depending on the listing. Final compensation details should be shown in the Compensation Details field.",
    do_not_promise: combineExpectations({
      expectations: [
        "Reliable attendance",
        "Professional jobsite behavior",
        "Safe tool use",
        "Clean work habits",
        "Respect for clients and property",
        "No unapproved scope changes",
        "Clear communication with supervisors",
      ],
    }),
    growth_path:
      "Skilled labor can grow into lead tech, maintenance tech, crew lead, superintendent assistant, superintendent, or specialized trade role.",
  },

  // ------------------------------------------------- SUBCONTRACTOR COORDINATOR ---
  "subcontractor-coordinator": {
    title: "Subcontractor Coordinator",
    overview:
      "Black Ridge Construction is looking for a subcontractor coordinator to help build, organize, communicate with, and manage relationships with trade partners and subcontractors. This role supports residential, commercial, roofing, maintenance, remodeling, exterior, and project listing opportunities by helping match the right partners to the right scopes.",
    responsibilities: bullets([
      "Source and organize subcontractor and trade partner information",
      "Review subcontractor applications and project opportunity submissions",
      "Help collect insurance, licenses, portfolios, references, and service area details",
      "Communicate with trade partners about open scopes and project requirements",
      "Help schedule site walks and trade meetings",
      "Track availability, pricing notes, trade categories, and performance notes",
      "Help maintain the subcontractor database",
      "Support project managers and superintendents with trade coordination",
      "Follow up with partners for missing documents or incomplete submissions",
      "Help post and manage project listings when assigned",
      "Keep communication professional, clear, and documented",
      "Help identify reliable partners for future Black Ridge work",
    ]),
    good_fit_for: bullets([
      "Organized communicators",
      "People comfortable talking to contractors and vendors",
      "People who can track details and documents",
      "People who understand construction trade categories",
      "People who can follow up without being disorganized",
      "People who can help build long-term partner relationships",
    ]),
    preferred_experience: bullets([
      "Construction coordination",
      "Vendor management",
      "Subcontractor communication",
      "Admin or office support",
      "Project coordination",
      "Scheduling",
      "CRM or spreadsheet management",
      "Insurance/document collection",
      "Trade partner recruiting",
    ]),
    compensation_notes:
      "Compensation may be hourly, salary, contract, part-time, full-time, or performance-based depending on the listing. Final compensation details should be shown in the Compensation Details field.",
    do_not_promise: combineExpectations({
      expectations: [
        "Keep partner information organized",
        "Communicate clearly",
        "Do not promise work without approval",
        "Do not award scopes without leadership approval",
        "Keep insurance and document status updated",
        "Support production without creating confusion",
      ],
    }),
    growth_path:
      "A subcontractor coordinator may grow into project coordinator, production coordinator, operations coordinator, trade partner manager, or project manager.",
  },
};

/**
 * Return the smart template for a category, or `null` if none exists yet
 * (e.g. for any future categories that haven't been authored yet).
 */
export function getSmartTemplate(category) {
  return SMART_TEMPLATES[category] || null;
}

// Status workflow for career applications (mirrors backend CAREER_APP_STATUSES).
export const CAREER_APP_STATUSES = [
  { slug: "new", label: "New", tone: "slate" },
  { slug: "reviewing", label: "Reviewing", tone: "amber" },
  { slug: "contacted", label: "Contacted", tone: "sky" },
  { slug: "interview-scheduled", label: "Interview Scheduled", tone: "violet" },
  { slug: "not-a-fit", label: "Not a Fit", tone: "red" },
  { slug: "hired", label: "Hired", tone: "emerald" },
  { slug: "archived", label: "Archived", tone: "gray" },
];

export const STATUS_LABEL = Object.fromEntries(
  CAREER_APP_STATUSES.map((s) => [s.slug, s.label])
);
